// Poofiz kit builder UI (<cozy-kit-builder>, snippets/cozy-kit-builder.liquid).
// Per slot: a swipe row of style cards (native scroll-snap; the arrows appear only when the row
// overflows), then the chosen style's colour/size radios. Keeps the step line, the summary and the
// add button in sync and announces changes in a polite live region.
// The price, the availability re-check and the cart add live in assets/cozy-kit-cart.js.
//
// Step mode (a narrow builder: phones, the product page column; CSS shows the tray only then): one step at a
// time under a tray of the three slots + the summary. A choice never moves on by itself (owner, 2026-10-09: the
// shopper taps through colours to see each photo first). Back / next buttons, the tray, the summary's "change"
// buttons and the kit CTAs move between steps; focus goes to the new step's heading and a polite announcement
// says where the shopper is.
//
// Sizes: a size (fieldset[data-kb-required]) starts with nothing checked. Until one is picked the step isn't
// complete (no tick in the tray) and the kit can't be added: "next" or an add without it shows
// and says what's missing, opens that step and focuses the size.
//
// Kit CTAs: any [data-kb-cta="<uid>"] on the page (the product page's first-screen button, the phone bar from the
// snippet's sticky param) follows the builder: "לבחירת הערכה" before the shopper starts, "הבא: <step>" while a step
// is open, "הוספת הערכה לסל" once every step is chosen (then a press adds the kit, same path as the add button).
// The phone bar shows only while none of the kit's own buttons is on screen, not over the footer, and never
// under an open dialog.

if (!customElements.get('cozy-kit-builder')) {
  // A photo URL (image_url, width 480) at another width.
  const sized = (url, width) => url.replace(/([?&])width=\d+/, `$1width=${width}`);

  customElements.define(
    'cozy-kit-builder',
    class CozyKitBuilder extends HTMLElement {
      connectedCallback() {
        const data = JSON.parse(this.querySelector('[data-kb-data]').textContent);
        this.kit = data.kit;
        this.slots = data.slots;
        this.strings = data.strings;
        this.live = this.querySelector('[data-kb-live]');
        this.addBtn = this.querySelector('[data-kb-add]');
        this.error = this.querySelector('[data-kb-error]');
        this.slotEls = [...this.querySelectorAll('[data-kb-slot]')];
        this.tray = this.querySelector('[data-kb-tray]');
        this.tslots = this.querySelector('[data-kb-tslots]');
        this.progress = this.querySelector('[data-kb-progress]');
        this.summaryEl = this.querySelector('[data-kb-summary-panel]');
        this.uid = this.dataset.kbUid || '';
        this.ctaSelector = `[data-kb-cta="${CSS.escape(this.uid)}"]`;
        this.current = 0;
        this.confirmed = this.slotEls.map(() => false);
        this.touched = this.slotEls.map(() => new Set());
        this.needShown = this.slotEls.map(() => false);
        this.engaged = false; // the shopper has started (a choice, a step button or a kit CTA)
        this.state = this.slotEls.map((el) => {
          const pressed = el.querySelector('[data-kb-style][aria-pressed="true"]');
          return { style: pressed ? Number(pressed.dataset.kbStyle) : 0, variant: null };
        });
        this.applyKitfixDefaults(); // kitfix 2026-10-21
        this.state.forEach((s, i) => (s.variant = this.resolveVariant(i)));
        // CRO 2026-10-20: every slot starts on a complete, available pick (the size on its default, see
        // snippets/cozy-kit-default-size.liquid): those count as chosen, so a kit CTA adds the kit in one tap.
        this.confirmed = this.slotEls.map((_, i) => this.slotOk(i) && !this.missing(i).length);
        // Follow-up 2026-10-20: a size is never preselected. A slot waiting only for its size doesn't hold the kit
        // CTA back: it still says "add", and a tap without the size adds nothing and leads to the size (add()).
        this.engaged = true;

        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        document.addEventListener('click', this.onCtaClick);
        this.initCarousels();
        this.initSticky();
        // (Re)connected (e.g. the theme editor moved the section): show the step the state says, step 1.
        [...this.slotEls, this.summaryEl].forEach((p, k) => {
          p?.classList.toggle('is-current', k === 0);
          p?.classList.remove('is-enter-fwd', 'is-enter-back');
        });
        this.wasStepMode = this.stepMode();
        this.modeObserver?.disconnect();
        this.modeObserver = new ResizeObserver(() => this.onModeChange());
        this.modeObserver.observe(this);
        this.defaultsChanged.forEach((i) => { this.updateChips(i); this.updateImage(i); });
        this.refresh(); // same text as the server render, so nothing moves
        this.initQuickSize(); // kitfix 2026-10-21
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.modeObserver?.disconnect();
        this.stickyObserver?.disconnect();
        this.dialogObserver?.disconnect();
        this.stickyMedia?.removeEventListener('change', this.onStickyMedia);
        document.removeEventListener('click', this.onCtaClick);
      }

      get calm() {
        return window.cozyCalm ? window.cozyCalm() : matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      style(i) {
        return this.slots[i].styles[this.state[i].style];
      }

      optionsEl(i) {
        return this.slotEls[i].querySelector(`[data-kb-options="${this.state[i].style}"]`);
      }

      // The variant matching the checked radios of the slot's current style (null = no such combination).
      resolveVariant(i) {
        const style = this.style(i);
        if (!style) return null;
        if (style.single) return style.variants[0] || null;
        const chosen = this.checkedValues(i);
        return style.variants.find((v) => v.options.every((o, k) => o === chosen[k])) || null;
      }

      checkedValues(i) {
        return [...this.optionsEl(i).querySelectorAll('[data-kb-fieldset]')].map(
          (fs) => fs.querySelector('input:checked')?.value
        );
      }

      // The slot's size fieldsets that have nothing checked yet (a size is never chosen for the shopper).
      missing(i) {
        return [...this.optionsEl(i).querySelectorAll('[data-kb-required]')].filter((fs) => !fs.querySelector('input:checked'));
      }

      // The pick's photo as [url at width 480, width, height]: the variant's own, else that of a variant with the
      // values chosen so far (a colour before its size), else the style's.
      imageOf(i) {
        const style = this.style(i);
        if (!style) return null;
        const v = this.state[i].variant;
        if (v) return style.images[v.img] || style.image || null;
        const chosen = this.checkedValues(i);
        const near = style.variants.find((x) => x.img !== undefined && x.options.every((o, k) => !chosen[k] || o === chosen[k]));
        return (near && style.images[near.img]) || style.image || null;
      }

      /* ---------- Events ---------- */

      onFocusIn = (e) => {
        this.lastFocus = e.target;
      };

      // The builder crossed the step/wide width (zoom, rotation). Into step mode: show the panel that holds the
      // focus (else the current one). Going wide shows every panel. Either way the kit CTAs follow (done() and
      // their plan depend on the mode).
      onModeChange() {
        const step = this.stepMode();
        if (step === this.wasStepMode) return;
        this.wasStepMode = step;
        if (step) {
          const panels = [...this.slotEls, this.summaryEl];
          const k = panels.findIndex((p) => p && this.lastFocus && p.contains(this.lastFocus));
          if (k >= 0) this.current = k;
          panels.forEach((p, m) => p?.classList.toggle('is-current', m === this.current));
          this.updateTray();
        }
        this.updateCtas();
      }

      // A kit CTA outside the builder: what it says now is what it does (ctaPlan).
      onCtaClick = (e) => {
        // CRO 2026-10-20: a part of the choices line next to a kit button opens that item's step.
        const pick = e.target.closest?.(`[data-kb-picks-for="${CSS.escape(this.uid)}"] [data-kb-picks-goto]`);
        if (pick) {
          e.preventDefault();
          this.engaged = true;
          this.openStep(Number(pick.dataset.kbPicksGoto));
          return;
        }
        const cta = e.target.closest?.(this.ctaSelector);
        if (!cta || cta.getAttribute('aria-busy') === 'true') return;
        e.preventDefault();
        const plan = this.ctaPlan();
        this.engaged = true;
        plan.run();
        this.updateCtas();
      };

      onClick = (e) => {
        if (!this.engaged && e.target.closest('button, input')) {
          this.engaged = true;
          queueMicrotask(() => this.updateCtas()); // "לבחירת הערכה" becomes the step's "next"
        }
        // A tap on an option counts as choosing it, even the one already checked (no 'change' then): the
        // default colour is a real choice too. The tray and the kit CTAs follow here (updateTray updates both):
        // without a 'change' nothing else does.
        const opt = e.target.closest('input[data-kb-opt]');
        if (opt) {
          const i = this.slotIndex(opt);
          this.touched[i].add(Number(opt.dataset.kbOpt));
          if (opt.checked) {
            setTimeout(() => this.updateTray());
          }
          return;
        }
        const go = e.target.closest('[data-kb-goto]');
        if (go) return this.goTo(Number(go.dataset.kbGoto));
        if (e.target.closest('[data-kb-back]')) return this.goTo(this.current - 1);
        if (e.target.closest('[data-kb-forward]')) return this.forward(this.current + 1);
        const card = e.target.closest('[data-kb-style]');
        if (card) return this.selectStyle(this.slotIndex(card), Number(card.dataset.kbStyle));
        const arrow = e.target.closest('[data-kb-prev], [data-kb-next]');
        if (arrow) return this.scrollRow(arrow);
        if (e.target.closest('[data-kb-add]')) this.add();
      };

      onChange = (e) => {
        if (!e.target.matches('[data-kb-opt]')) return;
        this.engaged = true;
        const i = this.slotIndex(e.target);
        this.state[i].soldNow = false;
        this.state[i].variant = this.resolveVariant(i);
        this.touched[i].add(Number(e.target.dataset.kbOpt));
        this.updateChips(i);
        this.updateImage(i);
        this.refresh();
        this.say(this.slotMessage(i, this.pickLine(i)));
      };

      slotIndex(el) {
        return Number(el.closest('[data-kb-slot]').dataset.kbSlot);
      }

      selectStyle(i, j) {
        const slotEl = this.slotEls[i];
        const card = slotEl.querySelector(`[data-kb-style="${j}"]`);
        if (!card || card.disabled) return;
        if (this.state[i].style !== j) {
          slotEl.querySelectorAll('[data-kb-style]').forEach((c) => c.setAttribute('aria-pressed', String(c === card)));
          slotEl.querySelectorAll('[data-kb-options]').forEach((o) => (o.hidden = o.dataset.kbOptions !== String(j)));
          this.state[i].style = j;
          this.state[i].soldNow = false;
          this.touched[i] = new Set(); // the new style's options start unchosen
          this.state[i].variant = this.resolveVariant(i);
          this.updateChips(i);
          this.updateImage(i);
          this.refresh();
        }
        card.scrollIntoView({ behavior: this.calm ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        this.say(this.slotMessage(i, this.strings.selected.replace('[title]', this.pickLine(i))));
      }

      /* ---------- Steps ---------- */

      stepMode() {
        return Boolean(this.tray) && getComputedStyle(this.tray).display !== 'none';
      }

      // Every option of the slot's style picked by the shopper (an option with one value counts as picked).
      complete(i) {
        const style = this.style(i);
        if (!style || style.single) return true;
        return [...this.optionsEl(i).querySelectorAll('[data-kb-fieldset]')].every(
          (fs, k) => this.touched[i].has(k) || fs.querySelectorAll('input[data-kb-opt]').length < 2
        );
      }

      // The slot counts as chosen (the kit CTAs): an available pick with its size, made in full by the shopper or
      // accepted by moving on (a preselected colour). Wide (every step on screen, no "next" to accept it), a valid
      // preselected pick counts as chosen: only a missing size keeps the step open there.
      done(i) {
        return this.slotOk(i) && !this.missing(i).length && (this.confirmed[i] || this.complete(i) || !this.stepMode());
      }

      // n: 0..2 = the slots, 3 = the summary. confirm: leaving this step forward accepts its choice (a size is
      // never accepted unchosen: done() and the tray still ask for it). quiet: the caller scrolls, focuses, says.
      goTo(n, { confirm = false, quiet = false } = {}) {
        const last = this.slotEls.length;
        n = Math.max(0, Math.min(last, Number.isFinite(n) ? n : 0));
        if (confirm && this.current < last) this.confirmed[this.current] = true;
        if (n === last) this.confirmed = this.confirmed.map(() => true); // the summary accepts every pick
        const prev = this.current;
        this.current = n;
        const panels = [...this.slotEls, this.summaryEl];
        panels.forEach((p, k) => {
          p.classList.toggle('is-current', k === n);
          p.classList.remove('is-enter-fwd', 'is-enter-back');
        });
        this.updateTray();
        if (this.stickyObserver) this.updateSticky();
        if (!this.stepMode()) return;
        if (prev !== n && !this.calm) {
          void panels[n].offsetWidth; // restart the entrance
          panels[n].classList.add(n > prev ? 'is-enter-fwd' : 'is-enter-back');
        }
        if (quiet) return;
        // Keep the tray and the top of the new step in view.
        const top = this.tray.getBoundingClientRect().top;
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;
        if (top < header || top > innerHeight * 0.45) {
          this.tray.scrollIntoView({ block: 'start', behavior: this.calm ? 'auto' : 'smooth' });
        }
        panels[n].querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
        this.say(
          n === last
            ? this.strings.summary
            : this.strings.step_said
                .replace('[n]', String(n + 1))
                .replace('[total]', String(last + 1))
                .replace('[slot]', this.slots[n].label)
        );
      }

      // The step's "next" (its own button, or a kit CTA on the step being chosen): never past a missing size.
      forward(n) {
        const i = this.current;
        if (i < this.slotEls.length && this.missing(i).length) return this.needChoice(i, 'next');
        this.goTo(n, { confirm: true });
      }

      // "next" or an add without the size: open its step, show and say what's missing, focus the size (its first
      // value; nothing gets checked). Pointer users see the step's highlighted size box and the message.
      needChoice(i, why) {
        const fs = this.missing(i)[0];
        // Follow-up 2026-10-20: "בחרו מידה לנעלי הבית" (slippers), else "בחרו <option>".
        const message =
          why === 'add'
            ? this.slots[i].key === 'slippers'
              ? 'בחרו מידה לנעלי הבית'
              : `בחרו ${fs.dataset.kbName || 'מידה'}`
            : this.strings.need_next.replace('[option]', fs.dataset.kbName || '').replace('[slot]', this.slots[i].label);
        this.needShown[i] = true;
        this.setText(this.slotEls[i].querySelector('[data-kb-need]'), message);
        if (this.stepMode() && this.current !== i) this.goTo(i, { quiet: true });
        this.refresh();
        fs.scrollIntoView({ block: 'center', behavior: this.calm ? 'auto' : 'smooth' });
        fs.querySelector('input[data-kb-opt]:not(:disabled)')?.focus({ preventScroll: true });
        this.say(message);
      }

      // A kit CTA's way to a step: step mode shows it (goTo scrolls to the tray); wide, every step is on screen.
      openStep(n) {
        if (this.stepMode()) return this.goTo(n);
        const panel = this.slotEls[n];
        panel.scrollIntoView({ block: 'start', behavior: this.calm ? 'auto' : 'smooth' });
        panel.querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
      }

      /* ---------- Kit CTAs and the phone bar ---------- */

      // kitfix 2026-10-21: start on the colours of the kit's main photo (grey hoodie, camel slippers, grey gloves - CRO 2026-10-24),
      // only when that colour has an available variant; otherwise the theme's first-available default stays. Sizes never preselected.
      applyKitfixDefaults() {
        this.defaultsChanged = [];
        const want = { hoodie: 'אפור', slippers: 'קמל', gloves: 'אפור' };
        this.slotEls.forEach((el, i) => {
          const target = want[this.slots[i]?.key];
          const style = this.style(i);
          if (!target || !style || style.single) return;
          const opts = this.optionsEl(i);
          if (!opts) return;
          const input = [...opts.querySelectorAll('input[data-kb-opt]')].find((r) => r.value === target);
          if (!input) return;
          const k = Number(input.dataset.kbOpt);
          if (!style.variants.some((v) => v.available && v.options[k] === target)) return;
          if (!input.checked) { input.checked = true; this.defaultsChanged.push(i); }
        });
      }

      quickSlot() {
        return this.slots.findIndex((s) => s.key === 'slippers');
      }

      // The size chips above the kit buttons ([data-kb-qsize-for]): a mirror of the slipper step's size radios.
      initQuickSize() {
        this.qsizeSelector = `[data-kb-qsize-for="${CSS.escape(this.uid)}"]`;
        this.qsizeBoxes = [...document.querySelectorAll(this.qsizeSelector)];
        if (!this.qsizeBoxes.length) return;
        this.qsizeBoxes.forEach((box) => box.addEventListener('click', (e) => {
          const b = e.target.closest('[data-kb-qsize]');
          if (!b || b.disabled) return;
          e.preventDefault();
          const radio = this.quickRadios().find((r) => r.value === b.dataset.kbQsize);
          if (!radio) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          box.classList.remove('is-need');
        }));
        this.renderQuickSize();
        this.updateCtas();
      }

      quickRadios() {
        const i = this.quickSlot();
        if (i < 0) return [];
        const fs = this.optionsEl(i)?.querySelector('[data-kb-required]');
        return fs ? [...fs.querySelectorAll('input[data-kb-opt]')] : [];
      }

      renderQuickSize() {
        if (!this.qsizeBoxes?.length) return;
        const i = this.quickSlot();
        const style = this.style(i);
        const radios = this.quickRadios();
        const sig = radios.map((r) => `${r.value}:${r.checked ? 1 : 0}`).join('|') + `#${this.state[i]?.style}#` + this.checkedValues(i).join('/');
        if (sig === this.qsizeSig) return;
        this.qsizeSig = sig;
        this.qsizeBoxes.forEach((box) => {
          const row = box.querySelector('[data-kb-qsize-row]');
          box.hidden = !radios.length;
          if (!row) return;
          row.replaceChildren(...radios.map((r) => {
            const k = Number(r.dataset.kbOpt);
            const chosen = this.checkedValues(i);
            const ok = style.variants.some((v) => v.available && v.options[k] === r.value && v.options.every((o, m) => m === k || !chosen[m] || o === chosen[m]));
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'cozy-kbq__chip';
            b.dataset.kbQsize = r.value;
            b.textContent = r.value.replaceAll('-', '\u2060-\u2060');
            b.setAttribute('aria-pressed', String(r.checked));
            if (!ok) { b.disabled = true; b.title = this.strings.combo_sold_out || ''; }
            return b;
          }));
        });
      }

      pointQuickSize() {
        const box = this.qsizeBoxes?.find((b) => b.getClientRects().length && b.getBoundingClientRect().bottom > 0 && b.getBoundingClientRect().top < innerHeight) || this.qsizeBoxes?.[0];
        if (!box) return;
        box.classList.remove('is-need'); void box.offsetWidth; box.classList.add('is-need');
        const r = box.getBoundingClientRect();
        if (r.top < 0 || r.bottom > innerHeight) box.scrollIntoView({ block: 'center', behavior: this.calm ? 'auto' : 'smooth' });
        box.querySelector('[data-kb-qsize]:not([disabled])')?.focus({ preventScroll: true });
      }

      // What a kit CTA says and does now. Every slot chosen: add. Not started: open the first step. On the step
      // being chosen (step mode): that step's "next", to the next unchosen step or the summary. Else: open the
      // first unchosen step; leaving a step on screen that is chosen (done) accepts it first, so the tray ticks it
      // (an untouched or incomplete step is never accepted).
      ctaPlan() {
        // kitfix 2026-10-21: with the slipper size chips next to the kit buttons, an unchosen size points up to them.
        const qs = this.quickSlot();
        if (qs >= 0 && this.missing(qs).length && document.querySelector(this.qsizeSelector)) {
          return { label: 'בחרו מידה לנעלי הבית ↑', run: () => this.pointQuickSize() };
        }
        const last = this.slotEls.length;
        const open = this.slotEls.map((_, i) => i).filter((i) => !this.done(i) && !(this.slotOkBut(i) && (this.missing(i).length || this.slotOk(i))));
        // Follow-up 2026-10-20: a full, available choice (style + size) is ready to add without visiting each step.
        if (!open.length) return { label: this.strings.add, run: () => this.add(), ready: true };
        if (!this.engaged) return { label: this.strings.cta_start, run: () => this.openStep(open[0]) };
        if (this.stepMode() && this.current === open[0]) {
          const after = open.find((k) => k > open[0]) ?? last;
          return { label: this.stepLabel(after), run: () => this.forward(after) };
        }
        return {
          label: this.stepLabel(open[0]),
          run: () => {
            if (this.stepMode() && this.current < last && this.done(this.current)) this.confirmed[this.current] = true;
            this.openStep(open[0]);
          },
        };
      }

      stepLabel(n) {
        const name = n < this.slotEls.length ? this.slots[n].label : this.strings.step_summary;
        return this.strings.next_step.replace('[slot]', name);
      }

      // The label goes in the CTA's [data-kb-cta-label] (else its first text span, else the CTA itself).
      updateCtas() {
        const { label, ready } = this.ctaPlan();
        if (ready) window.PoofizKit?.prefetch(this.kit, this.picks()); // every step chosen: warm the add's stock check
        document.querySelectorAll(this.ctaSelector).forEach((cta) => {
          const target =
            cta.querySelector('[data-kb-cta-label]') ||
            (cta.children.length ? cta.querySelector(':scope > span:not([aria-hidden="true"])') : null) ||
            cta;
          this.setText(target, label);
        });
      }

      // The phone bar (snippet param sticky): fixed, moved by transform only. Hidden and inert while it is not
      // wanted: wider than a phone, or one of the kit's own buttons (the step's next, the add button, another kit
      // CTA), the current step itself, or the footer is on screen. A dialog (cart, menu, search) only hides it: showModal already makes the
      // page inert, and an inert bar couldn't take back the focus the dialog returns on close. The first observer
      // callback sets it: no slide on load.
      initSticky() {
        this.sticky = document.querySelector(`[data-kb-sticky="${CSS.escape(this.uid)}"]`);
        if (!this.sticky) return;
        this.stickyMedia = matchMedia('(max-width: 749px)');
        this.inView = new Set();
        this.stickyObserver = new IntersectionObserver((entries) => {
          entries.forEach((en) => (en.isIntersecting ? this.inView.add(en.target) : this.inView.delete(en.target)));
          this.updateSticky();
        });
        [
          ...this.querySelectorAll('[data-kb-forward], [data-kb-add]'),
          ...this.slotEls,
          ...document.querySelectorAll(this.ctaSelector),
          document.querySelector('footer'),
        ]
          .filter((el) => el && !this.sticky.contains(el))
          .forEach((el) => this.stickyObserver.observe(el));
        this.dialogObserver = new MutationObserver(() => this.updateSticky());
        this.dialogObserver.observe(document.body, { attributes: true, attributeFilter: ['open'], subtree: true });
        this.onStickyMedia = () => this.updateSticky();
        this.stickyMedia.addEventListener('change', this.onStickyMedia);
      }

      updateSticky() {
        if (!this.sticky) return;
        // A step on screen counts only while it is the current one.
        const onScreen = [...this.inView].some((el) => !this.slotEls.includes(el) || el.classList.contains('is-current'));
        const wanted = this.stickyMedia.matches && !onScreen;
        const show = wanted && !document.querySelector('dialog[open]');
        if (this.sticky.classList.contains('is-visible') !== show) this.sticky.classList.toggle('is-visible', show);
        if (this.sticky.inert === wanted) this.sticky.inert = !wanted;
      }

      thumb(i) {
        const photo = this.imageOf(i);
        return photo ? sized(photo[0], 160) : '';
      }

      // The tray: where the shopper is, and a photo of each step they've chosen: accepted by moving on, or picked by
      // the shopper so that it counts as chosen (done(), as the kit CTAs say). Never an untouched step, never a step
      // without its size. Also the kit CTAs' label, which follows the same state.
      updateTray() {
        this.updateCtas();
        if (!this.tray) return;
        const last = this.slotEls.length;
        // Audit fix 2026-10-24: a check only on a completed step (passed, or picked in full); a preset default on a step
        // not reached yet is not "done" (this.confirmed still drives the kit CTA).
        const ticked = this.confirmed.map(
          (c, k) =>
            !this.missing(k).length &&
            ((c && (k < this.current || this.current >= last)) || (this.touched[k].size > 0 && this.done(k)))
        );
        this.tray.querySelectorAll('[data-kb-goto]').forEach((b) => {
          const k = Number(b.dataset.kbGoto);
          if (k === this.current) b.setAttribute('aria-current', 'step');
          else b.removeAttribute('aria-current');
          if (k >= last) return;
          b.classList.toggle('is-done', ticked[k]);
          this.setText(b.querySelector('[data-kb-tstate]'), `, ${ticked[k] || this.state[k].variant ? this.pickLine(k) : this.strings.not_chosen}`);
          const media = b.querySelector('[data-kb-tmedia]');
          const src = ticked[k] ? this.thumb(k) : '';
          let img = media.querySelector('img');
          if (src) {
            if (!img) {
              img = document.createElement('img');
              img.alt = '';
              img.width = 56;
              img.height = 56;
              img.decoding = 'async';
              media.append(img);
            }
            if (img.getAttribute('src') !== src) img.src = src;
          } else if (img) img.remove();
        });
        this.tslots?.style.setProperty('--kb-done', String(ticked.filter(Boolean).length));
        this.setText(
          this.progress,
          this.current >= last
            ? `${this.strings.progress.replace('[n]', String(last + 1)).replace('[total]', String(last + 1))} · ${this.strings.summary}`
            : this.strings.progress.replace('[n]', String(this.current + 1)).replace('[total]', String(last + 1))
        );
      }

      // What a slot change announces: the choice, plus the warning when it can't be added.
      slotMessage(i, text) {
        if (this.slotOk(i) || this.missing(i).length) return text;
        return `${text}. ${this.slotEls[i].querySelector('[data-kb-warn]').textContent}`;
      }

      /* ---------- Rendering ---------- */

      // Display only (the cart lines are built in cozy-kit-cart.js): a size range such as "38-39" keeps its
      // ASCII hyphen between word joiners. No line break inside it ("38- / ורוד"), and it still joins the two
      // numbers for the bidi algorithm (a U+2011 hyphen is neutral, so RTL text showed "39-38"). Same as the Liquid.
      // Without the size yet: the values chosen so far ("<title> · שחור"), same as the Liquid.
      pickLine(i) {
        const style = this.style(i);
        const v = this.state[i].variant;
        const joined = (text) => text.replaceAll('-', '\u2060-\u2060');
        if (style.single) return style.title;
        if (v) return `${style.title} · ${joined(v.title)}`;
        const partial = this.missing(i).length ? this.checkedValues(i).filter(Boolean).join(' / ') : '';
        return partial ? `${style.title} · ${joined(partial)}` : style.title;
      }

      // The slot's choice is fine apart from an unchosen size (colour/style picked or preselected).
      slotOkBut(i) {
        const style = this.style(i);
        return Boolean(style && style.variants.some((v) => v.available));
      }

      slotOk(i) {
        const v = this.state[i].variant;
        return Boolean(v && v.available);
      }

      chooseText(fs) {
        return this.strings.choose_option.replace('[option]', fs.dataset.kbName || '');
      }

      // Step lines, summary lines and photos, warnings and the add button. Writes only what changed.
      // The add button stays focusable when blocked (aria-disabled): pressing it says why. A missing size doesn't
      // block it: pressing it leads to the size (add).
      refresh() {
        this.slotEls.forEach((slotEl, i) => {
          const text = this.pickLine(i);
          const gaps = this.missing(i);
          this.setText(slotEl.querySelector('[data-kb-pick]'), text);
          this.setText(this.querySelector(`[data-kb-summary="${i}"]`), text);
          const lineNeed = this.querySelector(`[data-kb-line-need="${i}"]`);
          if (lineNeed) {
            this.setText(lineNeed, gaps.length ? this.chooseText(gaps[0]) : '');
            lineNeed.hidden = !gaps.length;
          }
          if (!gaps.length) this.needShown[i] = false;
          slotEl.classList.toggle('is-need', this.needShown[i]);
          slotEl.querySelector('[data-kb-need]').hidden = !this.needShown[i];
          const warn = slotEl.querySelector('[data-kb-warn]');
          const ok = this.slotOk(i) || gaps.length > 0; // an unchosen size isn't a sold-out combination
          if (!ok) this.setText(warn, this.state[i].soldNow ? this.strings.now_sold_out.replace('[slot]', this.slots[i].label) : this.strings.combo_sold_out);
          warn.hidden = ok;
          this.updateLineMedia(i);
        });
        this.updateSaving();
        this.updatePicks();
        this.renderQuickSize?.();
        const ready = !this.kitDown && this.state.every((_, i) => this.slotOk(i) || this.missing(i).length);
        this.addBtn.setAttribute('aria-disabled', String(!ready));
        if (ready) this.error.hidden = true;
        this.updateTray();
      }

      // CRO 2026-10-20: the choices line next to the kit buttons (snippets/cozy-kit-picks.liquid), same text rules.
      updatePicks() {
        const lines = document.querySelectorAll(`[data-kb-picks-for="${CSS.escape(this.uid)}"]`);
        if (!lines.length) return;
        const joined = (text) => text.replaceAll('-', '\u2060-\u2060');
        this.slotEls.forEach((_, i) => {
          const style = this.style(i);
          const v = this.state[i].variant;
          const gap = this.missing(i)[0];
          let text = style.single
            ? style.title
            : v
              ? joined(v.title)
              : [...this.checkedValues(i).filter(Boolean).map(joined), gap ? this.chooseText(gap) : ''].filter(Boolean).join(' · ');
          if (!style.single && v && this.slots[i].styles.length > 1) text = `${style.title} · ${text}`;
          lines.forEach((line) => this.setText(line.querySelector(`[data-kb-picks-slot="${i}"]`), text));
        });
      }

      blockedReason() {
        const reasons = this.kitDown ? [this.strings.kit_unavailable] : [];
        this.slotEls.forEach((slotEl, i) => {
          if (!this.slotOk(i)) reasons.push(`${this.slots[i].label}: ${slotEl.querySelector('[data-kb-warn]').textContent}`);
        });
        return reasons.join(' ');
      }

      // Saving against buying the picks separately: the chosen variant's price, or the style's lowest price
      // while a size is still open. The summary line and the product page's price row ([data-kb-save-for]).
      updateSaving() {
        const sep = this.slotEls.reduce((sum, _, i) => sum + (this.state[i].variant?.price ?? this.style(i).price ?? 0), 0);
        const save = sep - this.kit.price;
        const boxes = [this.querySelector('[data-kb-save]'), ...document.querySelectorAll(`[data-kb-save-for="${CSS.escape(this.uid)}"]`)];
        boxes.forEach((box) => {
          if (!box) return;
          box.hidden = save <= 0;
          if (save <= 0) return;
          this.setText(box.querySelector('[data-kb-sep]'), this.money(sep));
          this.setText(box.querySelector('[data-kb-save-text]'), (box.dataset.kbSaveTpl || this.strings.save_vs_separate).replace('[amount]', this.money(save)));
        });
      }

      // Shopify's money format with no trailing zeros, like the Liquid filter money_without_trailing_zeros.
      money(cents) {
        const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
        const whole = (cents / 100).toFixed(0);
        const group = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return (this.kit.money || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
          if (key === 'amount_no_decimals') return group(whole);
          if (key === 'amount_with_comma_separator') return amount.replace('.', ',');
          const [int, dec] = amount.split('.');
          return group(int) + (dec ? '.' + dec : '');
        });
      }

      setText(el, text) {
        if (el && el.textContent !== text) el.textContent = text;
      }

      // A value is available if some in-stock variant has it together with the other current choices (a size not
      // chosen yet matches any). The legend names the chosen value, or asks for the size.
      updateChips(i) {
        const style = this.style(i);
        if (style.single) return;
        const chosen = this.checkedValues(i);
        this.optionsEl(i)
          .querySelectorAll('[data-kb-fieldset]')
          .forEach((fs, k) => {
            fs.querySelectorAll('input[data-kb-opt]').forEach((input) => {
              const available = style.variants.some(
                (v) => v.available && v.options[k] === input.value && v.options.every((o, m) => m === k || !chosen[m] || o === chosen[m])
              );
              const label = input.closest('label');
              label.classList.toggle('is-unavailable', !available);
              label.querySelector('[data-kb-out]').hidden = available;
            });
            const value = fs.querySelector('[data-kb-opt-value]');
            const ask = !chosen[k] && fs.hasAttribute('data-kb-required');
            this.setText(value, ask ? this.chooseText(fs) : chosen[k] || '');
            value.classList.toggle('is-empty', ask);
          });
      }

      // The card follows the pick's photo (the chosen colour's own, else the style's), on a choice only. Same
      // widths and sizes as the server render; width/height from the photo. The photo box has a fixed ratio
      // (object-fit: cover), so nothing moves, and the old photo stays until the new one has loaded.
      updateImage(i) {
        const img = this.slotEls[i].querySelector(`[data-kb-style="${this.state[i].style}"] .cozy-kb__img`);
        const photo = this.imageOf(i);
        if (!img || !photo) return;
        const src = sized(photo[0], 480);
        if (img.getAttribute('src') === src) return;
        img.srcset = [160, 240, 320, 480].map((w) => `${sized(photo[0], w)} ${w}w`).join(', ');
        img.src = src;
        img.width = 480;
        img.height = Math.round((480 * photo[2]) / photo[1]);
      }

      // The summary line's photo: the same 160px file as the tray's.
      updateLineMedia(i) {
        const box = this.querySelector(`[data-kb-line-media="${i}"]`);
        if (!box) return;
        const src = this.thumb(i);
        let img = box.querySelector('img');
        if (!src) return img?.remove();
        if (!img) {
          img = document.createElement('img');
          img.alt = '';
          img.width = 48;
          img.height = 48;
          img.loading = 'lazy';
          img.decoding = 'async';
          box.append(img);
        }
        if (img.getAttribute('src') !== src) img.src = src;
      }

      say(message) {
        if (!this.live || !message) return;
        this.live.textContent = '';
        requestAnimationFrame(() => (this.live.textContent = message));
      }

      /* ---------- Swipe rows ---------- */

      initCarousels() {
        this.rows = [...this.querySelectorAll('[data-kb-carousel]')]
          .map((carousel) => ({
            track: carousel.querySelector('[data-kb-track]'),
            prev: carousel.querySelector('[data-kb-prev]'),
            next: carousel.querySelector('[data-kb-next]'),
          }))
          .filter((row) => row.prev && row.next);
        if (!this.rows.length) return;
        this.rows.forEach((row) => row.track.addEventListener('scroll', () => this.updateArrows(row), { passive: true }));
        this.resizeObserver = new ResizeObserver(() => this.rows.forEach((row) => this.updateArrows(row)));
        this.rows.forEach((row) => this.resizeObserver.observe(row.track));
      }

      // Arrows are absolutely positioned over the row, so showing them never moves anything.
      // RTL: scrollLeft runs from 0 (start) to negative values, so use its absolute value.
      updateArrows(row) {
        const { track, prev, next } = row;
        const max = track.scrollWidth - track.clientWidth;
        const overflow = max > 2;
        prev.hidden = next.hidden = !overflow;
        if (!overflow) return;
        const pos = Math.abs(track.scrollLeft);
        const focused = document.activeElement;
        prev.disabled = pos <= 2;
        next.disabled = pos >= max - 2;
        // A disabled arrow is hidden: never leave keyboard focus on it.
        if (focused === prev && prev.disabled) next.focus();
        if (focused === next && next.disabled) prev.focus();
      }

      scrollRow(arrow) {
        const row = this.rows.find((r) => r.prev === arrow || r.next === arrow);
        if (!row) return;
        const card = row.track.querySelector('[data-kb-style]');
        const gap = parseFloat(getComputedStyle(row.track).columnGap) || 0;
        const step = (card?.offsetWidth || row.track.clientWidth) + gap;
        const rtl = getComputedStyle(row.track).direction === 'rtl';
        const forward = arrow === row.next ? 1 : -1;
        row.track.scrollBy({ left: forward * step * (rtl ? -1 : 1), behavior: this.calm ? 'auto' : 'smooth' });
      }

      /* ---------- Add to cart ---------- */

      // The builder's add button and the kit CTAs (once every step is chosen) all add through here.
      async add() {
        if (this.addBtn.classList.contains('is-loading')) return;
        // A size never goes in by itself: lead to it.
        const gap = this.slotEls.findIndex((_, i) => this.missing(i).length);
        if (gap >= 0) return this.needChoice(gap, 'add');
        if (this.addBtn.getAttribute('aria-disabled') === 'true') {
          this.showError(this.blockedReason());
          return;
        }
        this.setBusy(true); // at once: the availability check below may take a round trip
        this.error.hidden = true;
        const picks = this.picks();
        try {
          const drawer = document.querySelector('cart-drawer');
          const sections = drawer?.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : [];
          const result = await window.PoofizKit.add({ kit: this.kit, picks, sections });
          if (!result.ok) {
            this.soldOutNow(result.problems, picks);
            return;
          }
          this.say(this.strings.added);
          const handled = !document.dispatchEvent(new CustomEvent('cozy:cart-add', { detail: result.data, cancelable: true }));
          if (!handled) window.location.href = window.routes?.cart_url || '/cart';
        } catch (err) {
          this.showError(err.userMessage || this.strings.error);
        } finally {
          this.setBusy(false);
        }
      }

      // The choices as cozy-kit-cart.js takes them (a pick that can't be bought has no variant).
      picks() {
        return this.state.map((s, i) => {
          const slot = this.slots[i];
          const style = this.style(i);
          return {
            key: slot.key,
            label: slot.label,
            handle: style.handle,
            title: style.title,
            single: style.single,
            variant: s.variant && s.variant.available ? s.variant : null,
          };
        });
      }

      // The fresh check found something sold out since the page loaded: mark exactly the variant
      // that was checked (the shopper may have changed the slot meanwhile) and say what.
      soldOutNow(problems, picks) {
        const messages = [];
        if (problems.includes('kit')) {
          this.kitDown = true;
          messages.push(this.strings.kit_unavailable);
        }
        this.slots.forEach((slot, i) => {
          if (!problems.includes(slot.key)) return;
          const checked = picks[i]?.variant;
          if (checked) checked.available = false; // same object as in the slot's data
          if (!checked || this.state[i].variant === checked) this.state[i].soldNow = true;
          this.updateChips(i);
          messages.push(this.strings.now_sold_out.replace('[slot]', slot.label));
        });
        this.refresh();
        this.showError(messages.join(' '));
      }

      // The error box is on the summary, under the add button; a kit CTA may add from anywhere (the phone bar, the
      // first-screen button). Step mode opens the summary. When the box is off screen (above the header, below the
      // viewport), the add button comes to the centre; the focus moves there from a kit CTA or from outside the
      // builder (the bar hides once the add button is on screen).
      showError(message) {
        const last = this.slotEls.length;
        if (this.stepMode() && this.current !== last) this.goTo(last, { quiet: true });
        this.error.textContent = message;
        this.error.hidden = false;
        const box = this.error.getBoundingClientRect();
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;
        if (box.top < header || box.bottom > innerHeight) {
          this.addBtn.scrollIntoView({ block: 'center', behavior: this.calm ? 'auto' : 'smooth' });
        }
        // From a CTA, from outside, or from a chip in a panel goTo() just hid: focus goes next to the message.
        const focus = document.activeElement;
        if (!focus || focus.closest('[data-kb-cta]') || !this.contains(focus) || !focus.getClientRects().length) {
          this.addBtn.focus({ preventScroll: true });
        }
      }

      setBusy(busy) {
        [this.addBtn, ...document.querySelectorAll(this.ctaSelector)].forEach((btn) => {
          btn.classList.toggle('is-loading', busy);
          if (busy) btn.setAttribute('aria-busy', 'true');
          else btn.removeAttribute('aria-busy');
        });
      }
    }
  );
}
